import React, { useEffect, useMemo, useState } from "react";
import { Alert, Form, Modal, Select, message } from "antd";
import dayjs from "dayjs";
import { fetchSprNumberCategories, fetchSprNumberList } from "../../sprs/number/spr-number-api";
import { docExpChangeNumber, fetchBronFreeNumbers } from "./doc-exp-api";

const pick = (obj, ...keys) => {
  for (const k of keys) {
    if (obj?.[k] != null) return obj[k];
  }
  return undefined;
};

/** Смена номера до заселения. Delphi: TFrmChangeNumber. */
const DocExpChangeNumberModal = ({ open, idDoc, row, onCancel, onDone }) => {
  const [form] = Form.useForm();
  const [numbers, setNumbers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [freeIds, setFreeIds] = useState(null);
  const [freeLoading, setFreeLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    Promise.all([fetchSprNumberList(), fetchSprNumberCategories()])
      .then(([list, cats]) => {
        const items = Array.isArray(list) ? list : pick(list, "items", "Items") || [];
        setNumbers(
          items
            .map((n) => ({
              value: pick(n, "idNumber", "IdNumber"),
              label: pick(n, "name", "Name"),
              idCat: pick(n, "idNumberCat", "IdNumberCat") || 0,
              childCount: pick(n, "childCount", "ChildCount") || 0
            }))
            .filter((n) => n.value && !n.childCount)
        );
        setCategories(
          (Array.isArray(cats) ? cats : [])
            .map((c) => ({
              value: pick(c, "idNumberCat", "IdNumberCat") || pick(c, "value"),
              label: pick(c, "name", "Name") || pick(c, "label")
            }))
            .filter((c) => c.value)
        );
        form.setFieldsValue({
          idNumberCat: row?.idNumberCat || undefined,
          idNumber: undefined
        });
      })
      .catch((e) => message.error(e.message || "Ошибка"));
  }, [open, form, row]);

  useEffect(() => {
    if (!open) return undefined;
    if (!row?.dateBeg || !row?.dateEnd) {
      setFreeIds(null);
      return undefined;
    }
    const ac = new AbortController();
    setFreeLoading(true);
    fetchBronFreeNumbers(
      {
        dateBeg: dayjs(row.dateBeg).format("YYYY-MM-DD"),
        dateEnd: dayjs(row.dateEnd).format("YYYY-MM-DD"),
        exceptDoc: idDoc
      },
      ac.signal
    )
      .then((ids) => {
        if (!ac.signal.aborted) setFreeIds(new Set((Array.isArray(ids) ? ids : []).map(Number)));
      })
      .catch((e) => {
        if (ac.signal.aborted) return;
        setFreeIds(null);
        message.error(e.message || "Не удалось получить свободные номера");
      })
      .finally(() => {
        if (!ac.signal.aborted) setFreeLoading(false);
      });
    return () => ac.abort();
  }, [open, idDoc, row?.dateBeg, row?.dateEnd]);

  const idNumberCat = Form.useWatch("idNumberCat", form);
  const options = useMemo(() => {
    const base = idNumberCat ? numbers.filter((n) => n.idCat === idNumberCat) : numbers;
    return base.filter((n) => n.value !== row?.idNumber && (!freeIds || freeIds.has(n.value)));
  }, [numbers, idNumberCat, row?.idNumber, freeIds]);

  const periodText =
    row?.dateBeg && row?.dateEnd
      ? `${dayjs(row.dateBeg).format("DD.MM.YYYY")} – ${dayjs(row.dateEnd).format("DD.MM.YYYY")}`
      : "";

  const onOk = async () => {
    try {
      const values = await form.validateFields();
      if (freeIds && !freeIds.has(values.idNumber)) {
        message.error("Номер занят на период путёвки");
        return;
      }
      setSubmitting(true);
      const saved = await docExpChangeNumber(idDoc, {
        idNumber: values.idNumber,
        idNumberCat: values.idNumberCat || 0
      });
      message.success("Номер изменён");
      onDone?.(saved);
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.message || "Не удалось сменить номер");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Сменить номер"
      open={open}
      onCancel={onCancel}
      onOk={onOk}
      confirmLoading={submitting}
      okButtonProps={{ disabled: freeLoading }}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item name="idNumberCat" label="Категория">
          <Select allowClear showSearch optionFilterProp="label" options={categories} />
        </Form.Item>
        <Form.Item
          name="idNumber"
          label="Новый номер"
          rules={[{ required: true, message: "Выберите номер" }]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            options={options}
            loading={freeLoading}
            placeholder={freeLoading ? "Проверяем свободные номера…" : "Выберите номер"}
            notFoundContent={freeLoading ? "Проверяем…" : "Нет свободных номеров на период"}
          />
        </Form.Item>
      </Form>
      {freeIds && periodText ? (
        <Alert
          type="info"
          showIcon
          message={`Показаны только номера, свободные на ${periodText} (${options.length})`}
        />
      ) : null}
    </Modal>
  );
};

export default DocExpChangeNumberModal;
